import { useCallback, useMemo, useState } from 'react';

/**
 * Client-side sort state for DataTable.
 *
 * Returns { sort, onSort }.  Pass both to <DataTable> and sort the row array
 * with applySort() — typically inside a useMemo — before passing to rows.
 *
 * Click behaviour:
 *   1st click  → ascending
 *   2nd click  → descending
 *   3rd click  → clear (back to natural/API order)
 */
export default function useSort(initialKey = null, initialOrder = 'asc') {
  const [sort, setSort] = useState(
    initialKey ? { key: initialKey, order: initialOrder } : null,
  );

  const onSort = useCallback((key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, order: 'asc' };
      if (prev.order === 'asc') return { key, order: 'desc' };
      return null; // third click clears
    });
  }, []);

  return { sort, onSort };
}

/**
 * Sort `rows` by the active sort column.  Returns the original array
 * reference when there is no active sort (avoids a useless re-render).
 *
 * Column objects may carry an optional `sortValue(row) => comparable`
 * function.  When absent, `row[column.key]` is used as the sort key.
 *
 * Nulls and undefineds always sort to the bottom regardless of direction,
 * so empty cells don't jump to the top when sorting ascending.
 *
 * Numbers are compared numerically; everything else uses localeCompare
 * with { numeric: true } so "10" > "9".
 */
export function applySort(rows, sort, columns) {
  if (!sort || !rows || rows.length === 0) return rows;
  const col = columns.find((c) => c.key === sort.key);
  if (!col || !col.sortable) return rows;
  const getValue = col.sortValue ?? ((row) => row[sort.key]);
  return [...rows].sort((a, b) => {
    const av = getValue(a);
    const bv = getValue(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;  // nulls last regardless of direction
    if (bv == null) return -1;
    const cmp =
      typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
    return sort.order === 'asc' ? cmp : -cmp;
  });
}

/**
 * Convenience wrapper: memoises applySort(rows, sort, columns).
 * Only useful when columns is a stable reference (module-level or useMemo).
 */
export function useSortedRows(rows, sort, columns) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => applySort(rows, sort, columns), [rows, sort]);
}
